import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../environments/environment';
import { IActionsSchemeFormItem, IDependsItem, IForm, iGroupItem, IReportFile, iSchemeTypes } from '../interfaces/schemes.interface';
import { IFormGroup } from '../interfaces/form-group.interface';
import { Observable, take } from 'rxjs';
import { IApiResponse2 } from '@interfaces/api-response.interface';
import { ISchemeResponse } from 'app/pages/panel/schemes-responses/schemes-responses.interface';
import { SubstituteCtData } from '@components/modal-substitute-ct/modal-substitute-ct.component';
import { MatDialog } from '@angular/material/dialog';
import { OpenModalConfirmService } from './open-modal-confirm.service';
import { ReportFilesDialogComponent } from '@components/modal-report-files/modal-report-files.component';
import { SchemeResponsesListService } from '@shared/scheme/scheme-responses-list/scheme-responses-list.service';

@Injectable({
  providedIn: 'root',
})
export class FormGroupService {
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);

  private selectedFormGroup = signal<iGroupItem | null>(null);
  private selectedFormGroupResponse = signal<ISchemeResponse | null>(null);
  private dialog = inject(MatDialog);
  private openModalConfirmService = inject(OpenModalConfirmService);
  private schemeResponsesListService = inject(SchemeResponsesListService);

  formFilterList = new FormGroup({
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    status: new FormControl(''),
    step: new FormControl(''),
    groupName: new FormControl(''),
    page: new FormControl(0),
    size: new FormControl(15),
    sortField: new FormControl(''),
    sortDirection: new FormControl(''),
  });

  setSelectedFormGroup(v: iGroupItem) {
    this.selectedFormGroup.set(v);
  }

  getSelectedFormGroup(): Signal<iGroupItem | null> {
    return this.selectedFormGroup.asReadonly();
  }
  getSelectedFormGroupResponse(): Signal<ISchemeResponse | null> {
    return this.selectedFormGroupResponse.asReadonly();
  }

  setSelectedFormGroupResponse(v: ISchemeResponse) {
    this.selectedFormGroupResponse.set(v);
  }

  list(options: {
    size: number;
    page: number;
    sortField?: string;
    sortDirection?: string;
    customerId: string;
    groupName?: string;
  }): Observable<IFormGroup> {
    let params = new HttpParams()
      .set('size', options.size)
      .set('page', options.page)
      .set('customerId', options.customerId);

    if (options.sortField) {
      params = params.set('sortField', options.sortField);
    }
    if (options.sortDirection) {
      params = params.set('sortDirection', options.sortDirection);
    }
    if (options.groupName) {
      params = params.set('groupName', options.groupName);
    }

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http.get<IFormGroup>(`${environment.api}/api/form-groups/search`, http);
  }

  register(payload: any) {
    if (payload.id) {
      return this.update(payload);
    } else {
      return this.create(payload);
    }
  }

  create(payload: any) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http.post(`${environment.api}/api/form-groups`, payload, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  update(payload: any) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http.put(`${environment.api}/api/form-groups/${payload.id}`, payload, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  delete(id: string) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      responseType: 'text' as const,
    };

    return this.http.delete(`${environment.api}/api/form-groups/${id}`, http);
  }

  getById(id: string) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http.get<iGroupItem>(`${environment.api}/api/form-groups/${id}`, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  responseRegisterFormGroup(payload: any, frontUrl?: string) {
    if (payload.id) {
      return this.updateResponseFormGroup(payload, frontUrl);
    } else {
      return this.createResponseFormGroup(payload);
    }
  }

  createResponseFormGroup(payload: any) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http.post(`${environment.api}/api/form-group-responses`, payload, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  updateResponseFormGroup(payload: any, route?: string) {
    const params = new HttpParams().set('frontUrl',`${environment.frontUrl}/login${route ? '?route=' + route : ''}`);

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };
    return this.http
      .put(`${environment.api}/api/form-group-responses/${payload.id}`, payload, http)
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  findResponseFormGroup(options: { customerId: string; reference: string }) {
    const params = new HttpParams()
      .set('customerId', options.customerId)
      .set('reference', options.reference);

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http.get(`${environment.api}/api/form-group-responses/find`, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  findResponseFormGroupById(id: string) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http
      .get<ISchemeResponse>(`${environment.api}/api/form-group-responses/${id}`, http)
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  enterpriseByClients(options: { page: number; size: number; customerCorporateName?: string }) {
    let params = new HttpParams().set('page', options.page).set('size', options.size);

    if (options.customerCorporateName) {
      params = params.set('customerCorporateName', options.customerCorporateName);
    }

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http
      .get(`${environment.api}/enterprise-service/list-enterprise-customer-paginated/dto`, http)
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  listResponseFormGroup(options: {
    page: number;
    size: number;
    customerId: string;
    reference: string;
  }) {
    const params = new HttpParams()
      .set('page', options.page)
      .set('size', options.size)
      .set('customerId', options.customerId)
      .set('reference', options.reference);

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http.get(`${environment.api}/api/form-group-responses/`, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  listResponseFormGroupFiltered(options: {
    page: number;
    size: number;
    sortField?: string;
    sortDirection?: string;
    document?: string;
    groupReference?: string;
    step?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    typeSchema?: iSchemeTypes;
    columnsQuestionCode?: iSchemeTypes;
    headersValue?: iSchemeTypes;
  }) {
    let params = new HttpParams().set('page', options.page).set('size', options.size);

    if (options.sortField) {
      params = params.set('sortField', options.sortField);
    }
    if (options.sortDirection) {
      params = params.set('sortDirection', options.sortDirection);
    }
    if (options.groupReference) {
      params = params.set('groupReference', options.groupReference);
    }

    if (options.typeSchema) {
      params = params.set('typeSchema', options.typeSchema);
    }

    if (options.document) {
      params = params.set('document', options.document);
    }
    if (options.step) {
      params = params.set('step', options.step);
    }
    if (options.status) {
      params = params.set('status', options.status);
    }
    if (options.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options.endDate) {
      params = params.set('endDate', options.endDate);
    }
    if (options.columnsQuestionCode) {
      params = params.set('columnsQuestionCode', options.columnsQuestionCode);
    }
    if (options.headersValue) {
      params = params.set('headersValue', options.headersValue);
    }

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http
      .get<IApiResponse2<ISchemeResponse>>(
        `${environment.api}/api/form-group-responses/step-search`,
        http
      )
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  getFormResponseById(id: string) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };

    return this.http.get(`${environment.api}/api/form-group-responses/${id}`, http).pipe(
      take(1),
      (res) => res,
      (error) => error
    );
  }

  deleteFormResponse(id: string) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      responseType: 'text' as const,
    };
    return this.http.delete(`${environment.api}/api/form-group-responses/${id}`, http);
  }

  findByCustomerId(customerId: string, userId?: string) {
    let params = new HttpParams().set('customerId', customerId);

    if (userId) {
      params = params.set('userId', userId);
    }
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http
      .get(`${environment.api}/enterprise-form-group-service/find-groups-by-customer`, http)
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  listByResponseValue(options: { document: string; value: any }) {
    const params = new HttpParams().set('document', options.document).set('value', options.value);

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http
      .get(`${environment.api}/form-response-model/find-by-response-value`, http)
      .pipe(
        (res) => res,
        (error) => error
      );
  }

  generateSubstituteCT(data: SubstituteCtData) {
    let params = new HttpParams();

    if (data.isSubstitute != null && data.isSubstitute != undefined) {
      params = params.set('isSubstitute', data.isSubstitute);
    }
    if (data.message) {
      params = params.set('message', data.message);
    }
    if (data.newCtCode) {
      params = params.set('newCtCode', data.newCtCode);
    }
    if (data.reason) {
      params = params.set('reason', data.reason);
    }
    if (data.reasonDescription) {
      params = params.set('reasonDescription', data.reasonDescription);
    }

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };

    return this.http.post(`${environment.api}/api/form-group-responses/clone-form-group-response/${data.id}`, {}, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  getQimaUsersByCustomer(customerId: string, typeSchema: iSchemeTypes) {
    const params = new HttpParams().set('customerId', customerId).set('typeSchema', typeSchema);
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
      params,
    };
    return this.http.get(`${environment.api}/enterprise-form-group-service/get-qima-users-by-customer`, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  setResponsiblesUsers(responseId: string, responsibles: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    fullName: string;
    jobPosition: string;
  }[]) {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
      }),
    };
    return this.http.patch(`${environment.api}/api/form-group-responses/${responseId}/responsible-user`, responsibles, http).pipe(
      (res) => res,
      (error) => error
    );
  }

  openModalFiles(formGroup: any, isAdmin = false, responseId: string) {
    let files = formGroup?.reportFilesList;
    if (!isAdmin) {
      files = files.filter((file: IReportFile) => file.type === 'DRAFT');
    }
    if (!files?.length) {
      this.openModalConfirmService.openModalConfirm({
        text: 'Nenhum arquivo disponível para este relatório.',
        type: 'error',
        hideCancelButton: true,
      });
      return;
    }
    if (files?.some((file: any) => file.name.toLowerCase().includes('demeter'))) {
      this.schemeResponsesListService.getResponseByIdAndQuestionCode(responseId, 'COD_DEMETER').subscribe({
        next: (res: any) => {
          let filteredFiles = files;
          if (res.value.value === false) {
            filteredFiles = files.filter((file: IReportFile) => !(file.name.toLowerCase()).includes('demeter'));
          }
          if (filteredFiles.length > 0) {
            this.openReportFilesDialog(filteredFiles, responseId);
          } else {
            this.openModalConfirmService.openModalConfirm({
              text: 'Nenhum arquivo disponível para este relatório.',
              type: 'error',
              hideCancelButton: true,
            });
          }
        },
        error: () => {
          const filteredFiles = files.filter((file: IReportFile) => !(file.name.toLowerCase()).includes('demeter'));
          if (filteredFiles.length > 0) {
            this.openReportFilesDialog(filteredFiles, responseId);
          } else {
            this.openModalConfirmService.openModalConfirm({
              text: 'Nenhum arquivo disponível para este relatório.',
              type: 'error',
              hideCancelButton: true,
            });
          }
        }
      });
    } else {
      this.openReportFilesDialog(files, responseId);
    }
  }

  openReportFilesDialog(files: IReportFile[], stepId: string) {
    this.dialog.open(ReportFilesDialogComponent, {
      width: '560px',
      data: {
        stepId,
        files,
      },
      autoFocus: false,
    });
  }

  createFormItem(item: IForm) {
    const formItem = this.fb.group({
      formCode: [item?.formCode ?? '', Validators.required],
      formName: [item?.formName ?? '', Validators.required],
      ordem: [item?.ordem ?? '', Validators.required],
      actions: this.fb.array([]),
      depends: this.fb.array([]),
      approverRoles: this.fb.array([]),
      tableVisible: [item?.tableVisible ?? false],
      aiAnalysis: [item?.aiAnalysis ?? true, Validators.required]
    });

    const actions = formItem.get('actions') as FormArray;
    actions.clear();
    if (item.actions?.length) {
      item.actions.forEach((action: IActionsSchemeFormItem) => {
        actions.push(this.fb.group({
          status: [action.status, Validators.required],
          step: [action.step, Validators.required],
          action: [action.action, Validators.required],
        }));
      });
    }

    const depends = formItem.get('depends') as FormArray;
    depends.clear();
    if (item.depends?.length) {
      item.depends.forEach((depend: IDependsItem) => {
        depends.push(this.fb.group({
          code: [depend.code, Validators.required],
          status: [depend.status, Validators.required],
          message: [depend.message],
        }));
      });
    }

    const approverRoles = formItem.get('approverRoles') as FormArray;
    approverRoles.clear();
    if (item.approverRoles?.length) {
      item.approverRoles.forEach(role => {
        approverRoles.push(new FormControl(role));
      });
    }

    return formItem;
  }
}
